import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Flame, Clock, Beef, Wheat, Droplets, Utensils, Loader2, Check, Star, BookOpen } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { FoodThumbnail } from '@/components/FoodThumbnail';
import { RecipeDetailModal } from '@/components/RecipeDetailModal';

const API = '/api';

function MacroPills({ cal, protein, carbs, fat, time }) {
  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      {cal && <span className="flex items-center gap-0.5 text-[10px] font-medium text-[#C67A5C] bg-[#C67A5C]/10 px-2 py-0.5 rounded-full"><Flame className="w-2.5 h-2.5" />{cal} cal</span>}
      {protein && <span className="flex items-center gap-0.5 text-[10px] font-medium text-[#4A90D9] bg-[#4A90D9]/10 px-2 py-0.5 rounded-full"><Beef className="w-2.5 h-2.5" />{protein}g P</span>}
      {carbs && <span className="flex items-center gap-0.5 text-[10px] font-medium text-[#E5A84B] bg-[#E5A84B]/10 px-2 py-0.5 rounded-full"><Wheat className="w-2.5 h-2.5" />{carbs}g C</span>}
      {fat && <span className="flex items-center gap-0.5 text-[10px] font-medium text-[#D4736A] bg-[#D4736A]/10 px-2 py-0.5 rounded-full"><Droplets className="w-2.5 h-2.5" />{fat}g F</span>}
      {time && <span className="flex items-center gap-0.5 text-[10px] font-medium text-[#7A7A72] bg-[#1A3A4A]/5 px-2 py-0.5 rounded-full"><Clock className="w-2.5 h-2.5" />{time} min</span>}
    </div>
  );
}

export function InteractiveMealCard({ name, cal, protein, carbs, fat, items, cook_time, source, sourceNote, tasteRating, postWorkout, imageUrl, linkedRecipeId }) {
  const { authHeaders } = useAuth();
  const navigate = useNavigate();
  const [logging, setLogging] = useState(false);
  const [logged, setLogged] = useState(false);
  const [showRecipe, setShowRecipe] = useState(false);

  const ingredientList = items ? items.split(',').map(s => s.trim()).filter(Boolean) : [];
  const hasRecipe = ingredientList.length >= 2 && cook_time;

  const handleLog = async () => {
    setLogging(true);
    try {
      await axios.post(`${API}/ai-coach/quick-log`, {
        name,
        calories: parseInt(cal) || 0,
        protein: parseInt(protein) || 0,
        carbs: parseInt(carbs) || 0,
        fat: parseInt(fat) || 0,
        meal_type: 'meal',
        items: items || '',
      }, authHeaders());
      setLogged(true);
      toast.success(`Logged: ${name}`);
    } catch {
      toast.error('Failed to log meal');
    }
    setLogging(false);
  };

  // Build macro summary line like prototype: "560 cal · 48g protein · 12g fat · 25 min prep"
  const macroLine = [
    cal && `${cal} cal`,
    protein && `${protein}g protein`,
    fat && `${fat}g fat`,
    cook_time && `${cook_time} min prep`,
  ].filter(Boolean).join(' · ');

  return (
    <div
      className="rounded-[20px] overflow-hidden"
      style={{ border: '1px solid #E8E3DB', background: '#FFFFFF' }}
      data-testid="interactive-meal-card"
    >
      {/* Full food photography hero area */}
      <div
        className="relative overflow-hidden"
        style={{ height: 150, cursor: linkedRecipeId ? 'pointer' : 'default' }}
        onClick={() => linkedRecipeId && navigate(`/recipe/${linkedRecipeId}`)}
      >
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={name}
            className="w-full h-full object-cover"
            data-testid="meal-card-db-image"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        ) : (
          <FoodThumbnail
            foodName={name}
            size="lg"
            className="!w-full !h-full !rounded-none"
            data-testid="meal-card-thumbnail"
          />
        )}
      </div>

      {/* Content area */}
      <div style={{ padding: '16px 18px' }}>
        <p style={{ fontSize: 16, margin: 0, fontWeight: 500, fontFamily: 'var(--font-sans)', color: '#1A1A18' }} data-testid="meal-card-name">
          {name}
        </p>
        <p style={{ fontSize: 12, margin: '5px 0 12px', color: '#9A9590', fontFamily: 'var(--font-sans)' }}>
          {macroLine}
        </p>
        <div className="flex items-center gap-2">
          {hasRecipe && (
            <button
              onClick={() => setShowRecipe(true)}
              data-testid="view-recipe-btn"
              style={{
                display: 'inline-block', padding: '8px 16px', borderRadius: 20,
                fontSize: 13, fontFamily: 'var(--font-sans)', cursor: 'pointer', border: 'none',
                background: '#F5F0E8', color: '#6A6560', fontWeight: 500,
                transition: 'all 0.2s',
              }}
            >
              View recipe
            </button>
          )}
          <button
            onClick={handleLog}
            disabled={logging || logged}
            data-testid="log-this-btn"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '8px 16px', borderRadius: 20,
              fontSize: 13, fontFamily: 'var(--font-sans)', cursor: 'pointer', border: 'none',
              background: logged ? '#5A8C6A' : '#C67A5C',
              color: '#FFFFFF', fontWeight: 500,
              transition: 'all 0.2s',
              opacity: logging ? 0.7 : 1,
            }}
          >
            {logged ? (
              <><Check className="w-3.5 h-3.5" /> Logged</>
            ) : logging ? (
              <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Logging...</>
            ) : (
              'Log this'
            )}
          </button>
        </div>
      </div>

      {showRecipe && (
        <RecipeDetailModal
          isOpen={showRecipe}
          onClose={() => setShowRecipe(false)}
          name={name}
          cal={cal}
          protein={protein}
          carbs={carbs}
          fat={fat}
          items={items}
          cook_time={cook_time}
        />
      )}
    </div>
  );
}

export { MacroPills };
